import { createPriceReductionTask } from './priceReduction';
import { ref, push, set, get, update, remove, query, orderByChild, equalTo } from 'firebase/database';
import { database } from '../firebaseConfig';
import { normalizeRole } from '../config/roles';
import { LIMITED_AGENT, LIMITED_AGENT_COAGENT } from '../config/roleAccess';

// Limited Agent deals always carry the team leader (Kelly) as co-agent: co-list on Seller/Landlord deals,
// co-selling on Buyer/Tenant deals. Pure so it can be unit tested.
export const coAgentKeyFor = (type) => (['Buyer', 'Tenant'].includes(type) ? 'coSellingAgents' : 'coListAgents');
export const withLeaderCoAgent = (data, type, ownerUid, leader = LIMITED_AGENT_COAGENT) => {
  if (!leader?.uid || leader.uid === ownerUid) return data;
  const key = coAgentKeyFor(type || data?.type);
  const list = Array.isArray(data?.[key]) ? data[key].filter(Boolean) : [];
  const onDeal = [...(data?.coListAgents || []), ...(data?.coSellingAgents || [])].some(a => a && a.uid === leader.uid);
  return onDeal ? data : { ...data, [key]: [...list, { uid: leader.uid, name: leader.name, email: leader.email }] };
};
const isLimitedOwner = async (userId) => {
  try { return normalizeRole((await get(ref(database, `users/${userId}/role`))).val()) === LIMITED_AGENT; } catch { return false; }
};

/**
 * Create a new transaction and save to Firebase
 * @param {string} userId - The user ID from Firebase Auth
 * @param {object} transactionData - The form data
 * @param {string} transactionType - Type: 'Seller', 'Buyer', 'Landlord', 'Tenant'
 * @returns {Promise<string>} - The transaction ID
 */
export const createTransaction = async (userId, transactionData, transactionType) => {
  try {
    const transactionsRef = ref(database, `users/${userId}/transactions`);

    const base = (await isLimitedOwner(userId)) ? withLeaderCoAgent(transactionData, transactionType, userId) : transactionData;
    const newTransaction = {
      ...base,
      type: transactionType,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    // push() generates a unique ID and creates the new record
    const newTransactionRef = await push(transactionsRef, newTransaction);

    await indexForTeam(userId, newTransactionRef.key, `${transactionData.firstName || ''} ${transactionData.lastName || ''}`.trim(), coAgentUidsOf(base));
    console.log(`${transactionType} transaction created with ID:`, newTransactionRef.key);
    return newTransactionRef.key;
  } catch (error) {
    console.error('Error creating transaction:', error);
    throw error;
  }
};

/**
 * Get all transactions for a user
 * @param {string} userId - The user ID from Firebase Auth
 * @returns {Promise<array>} - Array of transactions with IDs
 */
// Firebase hands back a list as an object when its keys are not 0..n (for example after contact 1 of 2
// was removed, or when a row was written by key). Pages call .map/.filter on these, so a deal saved that
// way crashed the transaction page every time it was opened. Always give pages a plain list.
const LIST_FIELDS = ['additionalContacts'];
export const normalizeTransaction = (tx) => {
  if (!tx) return tx;
  const out = { ...tx };
  LIST_FIELDS.forEach((f) => {
    const v = out[f];
    if (v && !Array.isArray(v) && typeof v === 'object') out[f] = Object.values(v).filter(Boolean);
    else if (Array.isArray(v)) out[f] = v.filter(Boolean);
  });
  return out;
};

export const getTransactions = async (userId) => {
  try {
    const transactionsRef = ref(database, `users/${userId}/transactions`);
    const snapshot = await get(transactionsRef);

    if (snapshot.exists()) {
      const data = snapshot.val();
      // Convert Firebase object to array with IDs
      return Object.keys(data).map(id => normalizeTransaction({
        id,
        ...data[id]
      }));
    }

    return [];
  } catch (error) {
    console.error('Error fetching transactions:', error);
    throw error;
  }
};

/**
 * Get a single transaction by ID
 * @param {string} userId - The user ID from Firebase Auth
 * @param {string} transactionId - The transaction ID
 * @returns {Promise<object>} - The transaction object
 */
export const getTransaction = async (userId, transactionId) => {
  try {
    const transactionRef = ref(database, `users/${userId}/transactions/${transactionId}`);
    const snapshot = await get(transactionRef);

    if (snapshot.exists()) {
      return normalizeTransaction({
        id: transactionId,
        ...snapshot.val()
      });
    }

    return null;
  } catch (error) {
    console.error('Error fetching transaction:', error);
    throw error;
  }
};

/**
 * Update an existing transaction
 * @param {string} userId - The user ID from Firebase Auth
 * @param {string} transactionId - The transaction ID
 * @param {object} updates - The fields to update
 * @returns {Promise<void>}
 */
export const updateTransaction = async (userId, transactionId, updates) => {
  try {
    const transactionRef = ref(database, `users/${userId}/transactions/${transactionId}`);

    // A Limited Agent's form re-saves its co-agent list; keep the team leader on it.
    let safeUpdates = updates;
    if (('coListAgents' in updates || 'coSellingAgents' in updates) && await isLimitedOwner(userId)) {
      const cur = (await get(transactionRef)).val() || {};
      const merged = withLeaderCoAgent({ ...cur, ...updates }, updates.type || cur.type, userId);
      safeUpdates = { ...updates, coListAgents: merged.coListAgents || updates.coListAgents || null, coSellingAgents: merged.coSellingAgents || updates.coSellingAgents || null };
      Object.keys(safeUpdates).forEach(k => safeUpdates[k] === undefined && delete safeUpdates[k]);
    }
    const updatedData = {
      ...safeUpdates,
      updatedAt: new Date().toISOString()
    };

    // Read the price before saving so a reduction can be detected.
    const before = 'listingPrice' in updates ? ((await get(transactionRef)).val() || {}) : null;
    await update(transactionRef, updatedData);
    if (before && (before.type || updates.type) === 'Seller') {
      try { await createPriceReductionTask(transactionId, before.listingPrice, updates.listingPrice, userId); } catch (e) { console.error('Price reduction task skipped', e); }
    }
    if ('coListAgents' in updates || 'coSellingAgents' in updates) {
      // Keep the team index in step so co-agents can see the agent's tasks.
      const indexRef = ref(database, `transactionIndex/${transactionId}`);
      const indexSnap = await get(indexRef);
      const freshSnap = await get(transactionRef);
      const map = coAgentMap(coAgentUidsOf(freshSnap.val()));
      if (indexSnap.exists()) await update(indexRef, { coAgentUids: Object.keys(map).length ? map : null });
    }
    console.log('Transaction updated:', transactionId);
  } catch (error) {
    console.error('Error updating transaction:', error);
    throw error;
  }
};

/**
 * Delete a transaction
 * @param {string} userId - The user ID from Firebase Auth
 * @param {string} transactionId - The transaction ID
 * @returns {Promise<void>}
 */
export const deleteTransaction = async (userId, transactionId) => {
  try {
    const transactionRef = ref(database, `users/${userId}/transactions/${transactionId}`);

    await remove(transactionRef);
    await clearTransactionTasks(userId, transactionId);
    console.log('Transaction deleted:', transactionId);
  } catch (error) {
    console.error('Error deleting transaction:', error);
    throw error;
  }
};

/**
 * Get transactions by type
 * @param {string} userId - The user ID from Firebase Auth
 * @param {string} transactionType - Type: 'Seller', 'Buyer', 'Landlord', 'Tenant'
 * @returns {Promise<array>} - Array of transactions of the specified type
 */
export const getTransactionsByType = async (userId, transactionType) => {
  try {
    const allTransactions = await getTransactions(userId);
    return allTransactions.filter(t => t.type === transactionType);
  } catch (error) {
    console.error('Error fetching transactions by type:', error);
    throw error;
  }
};

/**
 * Duplicate a transaction. Keeps the people, property and details, but clears every date
 * and any lost status, and does not copy tasks (the new deal starts with its own).
 * @returns {Promise<string>} the new transaction ID
 */
export const duplicateTransaction = async (userId, transaction, sourceId) => {
  const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
  const skip = ['id', 'createdAt', 'updatedAt', 'status', 'lostReason', 'lostDetail', 'lostAt', 'duplicatedFrom'];
  const copy = {};
  Object.entries(transaction || {}).forEach(([key, value]) => {
    if (skip.includes(key)) return;
    if (typeof value === 'string' && DATE_RE.test(value)) return;
    if (/date|deadline/i.test(key)) return;
    if (value === undefined || value === null) return;
    copy[key] = value;
  });
  // Firebase rejects undefined values, so strip anything left that is not plain data.
  const clean = (await isLimitedOwner(userId)) ? withLeaderCoAgent(JSON.parse(JSON.stringify(copy)), copy.type, userId) : JSON.parse(JSON.stringify(copy));
  const now = new Date().toISOString();
  const newRef = await push(ref(database, `users/${userId}/transactions`), {
    ...clean,
    duplicatedFrom: sourceId || null,
    createdAt: now,
    updatedAt: now,
  });
  await indexForTeam(userId, newRef.key, `${copy.firstName || ''} ${copy.lastName || ''}`.trim(), coAgentUidsOf(clean));
  return newRef.key;
};

/**
 * Removes a transaction's tasks from every task list: the shared deal tasks, the
 * team index entry (so the TC no longer sees it), and any personal tasks tied to it.
 */
export const clearTransactionTasks = async (userId, transactionId) => {
  const updates = {};
  updates[`transactions/${transactionId}/tasks`] = null;
  updates[`transactionIndex/${transactionId}`] = null;
  const personalSnap = await get(ref(database, `users/${userId}/tasks`));
  if (personalSnap.exists()) {
    Object.entries(personalSnap.val() || {}).forEach(([taskId, task]) => {
      if (task?.transactionId === transactionId) updates[`users/${userId}/tasks/${taskId}`] = null;
    });
  }
  await update(ref(database), updates);
};

/**
 * Mark a transaction as lost, with the reason it was lost. Its tasks are removed.
 */
export const markTransactionLost = async (userId, transactionId, { reason, detail }) => {
  await updateTransaction(userId, transactionId, {
    status: 'lost',
    lostReason: reason,
    lostDetail: detail || '',
    lostAt: new Date().toISOString(),
  });
  await clearTransactionTasks(userId, transactionId);
};

/**
 * Lets the shared team (TC, Marketing Manager, Local Assistant) see this RESIDENCE deal's tasks.
 * Only RESIDENCE deals are indexed. A user with no organization on file counts as RESIDENCE.
 */
// Co-agent UIDs on a deal: seller co-listing agents and buyer co-selling agents.
export const coAgentUidsOf = (data) => [...(data?.coListAgents || []), ...(data?.coSellingAgents || [])]
  .map(a => a?.uid)
  .filter(Boolean);

// Stored as a map keyed by uid so the database rules can check one co-agent directly.
const coAgentMap = (uids) => Object.fromEntries([...new Set(uids || [])].map(u => [u, true]));

export const indexForTeam = async (userId, txId, label, coAgentUids = []) => {
  try {
    const orgSnap = await get(ref(database, `users/${userId}/organization`));
    const org = orgSnap.exists() ? orgSnap.val() : 'RESIDENCE';
    if (org !== 'RESIDENCE') return;
    // update (not set) so an existing sharedWith list is kept
    await update(ref(database, `transactionIndex/${txId}`), {
      ownerUid: userId,
      organization: org,
      label: label || '',
      createdAt: new Date().toISOString(),
      coAgentUids: coAgentMap(coAgentUids),
    });
  } catch (err) {
    console.error('Could not add transaction to the team index', err);
  }
};

// Adds the user's existing RESIDENCE deals to the team index (fills in deals made before this existed).
export const syncOwnTransactionIndex = async (userId, transactions) => {
  await Promise.all((transactions || []).filter(tx => tx?.id).map(tx =>
    indexForTeam(userId, tx.id, `${tx.firstName || ''} ${tx.lastName || ''}`.trim(), coAgentUidsOf(tx))));
};

// All deals this user can see: their own, plus RESIDENCE deals for the TC (Tina).
// Support roles see every RESIDENCE deal, not only their own.
export const SUPPORT_ROLES = ['TC', 'Marketing Manager', 'Local Assistant'];

// Whether another person's deal (by its team index entry) belongs in this user's list.
// Support roles see every RESIDENCE deal; everyone sees deals shared with them and deals where
// they are a co-list or co-selling agent (any role, not only Limited Agents).
export const indexEntryVisibleTo = (entry, uid, seesAll = false) => Boolean(
  entry && uid && entry.ownerUid !== uid && (seesAll || entry.sharedWith?.[uid] || entry.coAgentUids?.[uid])
);

export const getTeamTransactions = async (uid, userRole) => {
  const own = ((await getTransactions(uid)) || []).map(t => ({ ...t, ownerUid: uid }));
  const indexSnap = await get(ref(database, 'transactionIndex'));
  if (!indexSnap.exists()) return own;
  const seen = new Set(own.map(t => t.id));
  const all = SUPPORT_ROLES.includes(userRole);
  // Load the other deals in parallel instead of one at a time.
  const wanted = Object.entries(indexSnap.val())
    .filter(([id, entry]) => !seen.has(id) && indexEntryVisibleTo(entry, uid, all));
  const loaded = await Promise.all(wanted.map(([id, entry]) =>
    getTransaction(entry.ownerUid, id).then(tx => (tx ? { ...tx, ownerUid: entry.ownerUid } : null))
  ));
  return [...own, ...loaded.filter(Boolean)];
};

// One deal by ID, whether it is this user's or a RESIDENCE deal the TC can see.
export const getTransactionForUser = async (uid, transactionId, userRole) => {
  const own = await getTransaction(uid, transactionId);
  if (own) return { ...own, ownerUid: uid };
  const entrySnap = await get(ref(database, `transactionIndex/${transactionId}`));
  if (!entrySnap.exists()) return null;
  const entry = entrySnap.val();
  // The TC sees all RESIDENCE deals; a co-agent sees the deals they are on.
  const canSee = SUPPORT_ROLES.includes(userRole) || userRole === 'Admin' || Boolean(entry.coAgentUids?.[uid]) || Boolean(entry.sharedWith?.[uid]);
  if (!canSee) return null;
  const tx = await getTransaction(entry.ownerUid, transactionId);
  return tx ? { ...tx, ownerUid: entry.ownerUid } : null;
};

// ---- Sharing and transfer (Kelly's access model) ----
// Share: the deal also appears in the other person's Transactions list; they can see and work it.
// Only the owner or an Admin can share, un-share or transfer.
const logDeal = (owner, txId, text, uid) => push(ref(database, `users/${owner}/transactions/${txId}/accessLog`), { text, by: uid || null, at: new Date().toISOString() });

export const shareTransaction = async (tx, { add = [], remove: rem = [] }, uid, nameOf = (u) => u) => {
  const owner = tx.ownerUid || uid;
  const ops = {};
  add.filter(u => u && u !== owner).forEach(u => { ops[`transactionIndex/${tx.id}/sharedWith/${u}`] = true; ops[`users/${owner}/transactions/${tx.id}/sharedWith/${u}`] = true; });
  rem.forEach(u => { ops[`transactionIndex/${tx.id}/sharedWith/${u}`] = null; ops[`users/${owner}/transactions/${tx.id}/sharedWith/${u}`] = null; });
  if (!Object.keys(ops).length) return;
  const idx = await get(ref(database, `transactionIndex/${tx.id}`));
  if (!idx.exists()) await indexForTeam(owner, tx.id, `${tx.firstName || ''} ${tx.lastName || ''}`.trim(), coAgentUidsOf(tx));
  await update(ref(database), ops);
  const text = [add.length && `Shared with ${add.map(nameOf).join(', ')}`, rem.length && `Stopped sharing with ${rem.map(nameOf).join(', ')}`].filter(Boolean).join('; ');
  await logDeal(owner, tx.id, text, uid).catch(() => {});
};

// Transfer: the whole deal (documents list, notes, everything under it) moves to the new owner's
// Transactions, and the previous owner loses access unless keepPrevious is true (then it is shared).
export const transferTransaction = async (tx, toUid, uid, { keepPrevious = false, nameOf = (u) => u } = {}) => {
  const from = tx.ownerUid || uid;
  if (!toUid || toUid === from) return;
  const snap = await get(ref(database, `users/${from}/transactions/${tx.id}`));
  if (!snap.exists()) throw new Error('Deal not found');
  const data = snap.val();
  const shared = { ...(data.sharedWith || {}) };
  delete shared[toUid];
  if (keepPrevious) shared[from] = true;
  data.sharedWith = Object.keys(shared).length ? shared : null;
  data.transferredFrom = from; data.transferredAt = new Date().toISOString();
  await update(ref(database), {
    [`users/${toUid}/transactions/${tx.id}`]: data,
    [`users/${from}/transactions/${tx.id}`]: null,
    [`transactionIndex/${tx.id}/ownerUid`]: toUid,
    [`transactionIndex/${tx.id}/sharedWith`]: data.sharedWith,
  });
  await logDeal(toUid, tx.id, `Transferred from ${nameOf(from)} to ${nameOf(toUid)}${keepPrevious ? ` (${nameOf(from)} kept shared access)` : ''}`, uid).catch(() => {});
};
